import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { Appointment, Office, SlotsResponse } from '../../lib/types';
import { ErrorNote, Field } from '../../ui/components';

function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Book a new appointment on behalf of a citizen calling or walking in without app access. */
export function BookAppointmentForm({ office }: { office: Office }) {
  const [serviceCode, setServiceCode] = useState(office.services[0] ?? '');
  const [date, setDate] = useState(todayISODate());
  const [personId, setPersonId] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [slots, setSlots] = useState<string[] | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booked, setBooked] = useState<Appointment | null>(null);
  const [bookError, setBookError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);

  const fetchSlots = async () => {
    if (!serviceCode) return;
    setLoadingSlots(true);
    setSlotsError(null);
    setSelectedSlot(null);
    setBooked(null);
    try {
      const res = await api.get<SlotsResponse>(
        `/api/v1/servicedesk/appointments/slots?officeCode=${office.code}&serviceCode=${serviceCode}&from=${date}&to=${date}`,
      );
      setSlots(res.slots);
    } catch (e) {
      setSlots(null);
      setSlotsError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not load slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const book = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    setBookError(null);
    try {
      const appt = await api.post<Appointment>('/api/v1/servicedesk/appointments', {
        officeCode: office.code,
        serviceCode,
        personId,
        slot: selectedSlot,
        caseRef: caseRef || undefined,
      });
      setBooked(appt);
      setSlots(null);
      setSelectedSlot(null);
    } catch (e) {
      setBookError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Book a new appointment</h2>
      <p className="mb-3 text-xs text-slate-500">For a citizen calling or walking in without access to the booking app.</p>

      {bookError ? (
        <div className="mb-3">
          <ErrorNote message={bookError} />
        </div>
      ) : null}

      {booked ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-800">
          <div className="font-semibold">Booked — {formatDateTime(booked.slot)}</div>
          <div className="mt-1 text-xs">
            Service {booked.serviceCode} for {booked.personId}. QR token: <code className="rounded bg-white/60 px-1 py-0.5">{booked.qrToken}</code>
          </div>
          <button className="btn-secondary mt-3 !bg-white" onClick={() => setBooked(null)}>
            Book another
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service">
              <select
                className="field-input"
                value={serviceCode}
                onChange={(e) => {
                  setServiceCode(e.target.value);
                  setSlots(null);
                }}
              >
                {office.services.length === 0 ? <option value="">No services configured</option> : null}
                {office.services.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date">
              <input
                type="date"
                className="field-input"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setSlots(null);
                }}
              />
            </Field>
          </div>
          <Field label="Citizen NIN / reference">
            <input className="field-input" value={personId} onChange={(e) => setPersonId(e.target.value)} placeholder="e.g. SC870512033" />
          </Field>
          <Field label="Case reference (optional)">
            <input className="field-input" value={caseRef} onChange={(e) => setCaseRef(e.target.value)} placeholder="e.g. IMM/2026/NIDR/0142" />
          </Field>

          <button className="btn-secondary" onClick={fetchSlots} disabled={!serviceCode || loadingSlots}>
            {loadingSlots ? 'Loading slots…' : 'Find open slots'}
          </button>

          {slotsError ? <ErrorNote message={slotsError} /> : null}

          {slots ? (
            slots.length === 0 ? (
              <div className="rounded-lg bg-slate-100 px-3.5 py-2.5 text-sm text-slate-600">No open slots that day — try another date.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {slots.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedSlot(s)}
                    className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold tabular-nums transition ${
                      selectedSlot === s
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400'
                    }`}
                  >
                    {new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                  </button>
                ))}
              </div>
            )
          ) : null}

          <button
            className="btn-primary w-full"
            onClick={book}
            disabled={booking || !selectedSlot || personId.trim().length < 3}
          >
            {booking ? 'Booking…' : selectedSlot ? `Book ${new Date(selectedSlot).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}` : 'Select a slot first'}
          </button>
        </div>
      )}
    </div>
  );
}
